import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, ImageIcon, FileSpreadsheet, Download } from "lucide-react";
import { compressImageFile } from "../../lib/imageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VISUALS = ["bottle", "oil", "spray", "dispenser", "tube", "tin", "bar", "kitbox", "comb", "box", "jerrycan", "mop", "dustpan", "bucket", "machine", "diffuser"];

const empty = { name: "", category: "guest-amenities", pack_size: "", moq: "", price: "", description: "", application: "", group: "", visual: "bottle" };

export const ProductForm = ({ token, onCreated }) => {
  const [form, setForm] = useState(empty);
  const [categories, setCategories] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const downloadTemplate = () => {
    const csv =
      "category,name,pack_size,moq,price,description,application\n" +
      "housekeeping-chemicals,Example Carpet Cleaner,5 L can,20 cans,₹450 / can,Short product description,Application area\n" +
      "guest-amenities,Example Body Lotion,30 ml tube,500 units,₹12 / unit,Short product description,Guest rooms\n";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "zalta-bulk-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const uploadBulk = async () => {
    if (!bulkFile) {
      toast.error("Pehle .xlsx ya .csv file chunein");
      return;
    }
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      const { data } = await axios.post(`${API}/admin/products/bulk`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${data.added} products added — ab website par live hain`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows skipped: ${data.errors[0]}`);
      setBulkFile(null);
      onCreated?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Bulk upload failed");
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/catalogue`).then((res) => setCategories(res.data.categories)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      setPreview("");
      return;
    }
    const compressed = await compressImageFile(f);
    setFile(compressed);
    setPreview(URL.createObjectURL(compressed));
    if (compressed.size < f.size) {
      toast.success(`Photo compress ho gayi: ${(f.size / 1048576).toFixed(1)} MB → ${(compressed.size / 1024).toFixed(0)} KB — upload fast hoga`);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let image_path = "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await axios.post(`${API}/admin/upload`, fd, { headers });
        image_path = up.data.path;
      }
      await axios.post(`${API}/admin/products`, { ...form, image_path }, { headers });
      toast.success(`${form.name} added — it is now live on the website`);
      setForm(empty);
      setFile(null);
      setPreview("");
      onCreated?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add product");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-[#FAF8FC] border border-[#221533]/15 rounded-md px-4 py-3 text-sm focus:border-[#8E5A9E] transition-colors";

  return (
    <div>
    <form onSubmit={submit} data-testid="add-product-form" className="bg-white border border-[#221533]/10 rounded-xl p-5 sm:p-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div className="space-y-4">
        <div>
          <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">Category *</label>
          <select data-testid="product-category-select" value={form.category} onChange={set("category")} className={`${inputCls} appearance-none`}>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">Product Name *</label>
          <input data-testid="product-name-input" required value={form.name} onChange={set("name")} placeholder="e.g. ZALTA Carpet Shampoo" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">Pack Size</label>
            <input data-testid="product-packsize-input" value={form.pack_size} onChange={set("pack_size")} placeholder="5 L can" className={inputCls} />
          </div>
          <div>
            <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">MOQ</label>
            <input data-testid="product-moq-input" value={form.moq} onChange={set("moq")} placeholder="20 cans" className={inputCls} />
          </div>
        </div>
        <div>
          <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">Price (optional)</label>
          <input data-testid="product-price-input" value={form.price} onChange={set("price")} placeholder="e.g. ₹450 / 5 L can" className={inputCls} />
        </div>
        <div>
          <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">Description</label>
          <textarea data-testid="product-description-input" rows={3} value={form.description} onChange={set("description")} placeholder="Short product description…" className={`${inputCls} resize-none`} />
        </div>
        <div>
          <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">Sub-category / Group (optional — same group ke products ek saath dikhte hain)</label>
          <input data-testid="product-group-input" value={form.group} onChange={set("group")} placeholder="e.g. Kitchen Care, Floor Care" list="group-suggestions" className={inputCls} />
          <datalist id="group-suggestions">
            {[...new Set((categories.find((c) => c.slug === form.category)?.products || []).map((p) => p.group).filter(Boolean))].map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </div>
        <div>
          <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">Application</label>
          <input data-testid="product-application-input" value={form.application} onChange={set("application")} placeholder="Corridors, wards & lobbies" className={inputCls} />
        </div>
        <div>
          <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">Icon Style (used when no photo)</label>
          <select data-testid="product-visual-select" value={form.visual} onChange={set("visual")} className={`${inputCls} appearance-none`}>
            {VISUALS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>

      <div className="flex flex-col">
        <label className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-2">Product Photo</label>
        <label
          data-testid="product-photo-drop"
          className="flex-1 min-h-[220px] border-2 border-dashed border-[#8E5A9E]/30 hover:border-[#8E5A9E] rounded-xl bg-[#FAF8FC] flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors overflow-hidden"
        >
          {preview ? (
            <img src={preview} alt="preview" className="w-full h-full object-contain max-h-[280px]" />
          ) : (
            <>
              <ImageIcon size={32} className="text-[#8E5A9E]/50" />
              <span className="text-sm text-[#221533]/50">Click to choose a photo (max 5 MB)</span>
            </>
          )}
          <input data-testid="product-photo-input" type="file" accept="image/*" className="hidden" onChange={pickFile} />
        </label>
        <button
          data-testid="product-submit-btn"
          type="submit"
          disabled={saving}
          className="mt-5 inline-flex items-center justify-center gap-2 bg-[#8E5A9E] hover:bg-[#74427F] disabled:opacity-50 text-white font-mono2 text-xs tracking-[0.25em] uppercase px-6 py-4 rounded-md transition-colors active:scale-[0.98]"
        >
          <Upload size={14} /> {saving ? "Publishing…" : "Publish Product"}
        </button>
      </div>
    </form>

    <div data-testid="bulk-upload-section" className="mt-6 bg-white border border-[#221533]/10 rounded-xl p-5 sm:p-8">
      <div className="flex items-center gap-3 mb-2">
        <FileSpreadsheet size={20} className="text-[#8E5A9E]" />
        <p className="font-clash text-xl font-semibold text-[#221533]">Bulk Upload — Excel / CSV</p>
      </div>
      <p className="text-sm text-[#221533]/55 font-light mb-5">
        Ek saath kai products add karein. Columns: <span className="font-mono2 text-xs">category, name, pack_size, moq, price, description, application</span>
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          data-testid="bulk-file-input"
          type="file"
          accept=".xlsx,.csv"
          onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
          className="flex-1 text-sm text-[#221533]/60 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:bg-[#8E5A9E]/10 file:text-[#74427F] file:font-semibold hover:file:bg-[#8E5A9E]/20 file:cursor-pointer"
        />
        <button
          data-testid="bulk-upload-btn"
          onClick={uploadBulk}
          disabled={bulkBusy}
          className="inline-flex items-center justify-center gap-2 bg-[#8E5A9E] hover:bg-[#74427F] disabled:opacity-50 text-white font-mono2 text-xs tracking-[0.2em] uppercase px-6 py-3.5 rounded-md transition-colors active:scale-95"
        >
          <Upload size={14} /> {bulkBusy ? "Uploading…" : "Upload & Publish"}
        </button>
        <button
          data-testid="bulk-template-btn"
          onClick={downloadTemplate}
          className="inline-flex items-center justify-center gap-2 border border-[#8E5A9E]/40 text-[#74427F] hover:bg-[#8E5A9E]/10 font-mono2 text-xs tracking-[0.2em] uppercase px-6 py-3.5 rounded-md transition-colors active:scale-95"
        >
          <Download size={14} /> Template
        </button>
      </div>
    </div>
    </div>
  );
};
